<template>
  <div :class="$style.collapsorWrapper">
    <div :class="$style.headerSlot" @click="toggleContent">
      <div :class="$style.titleSection">
        <Checkbox
          v-if="selectable"
          :model-value="selectionState === 'all'"
          :indeterminate="selectionState === 'some'"
          binary
          @update:model-value="emit('toggle-select-campaign', $event)"
          @click.stop
        />
        <p>{{ campaign.name }} ({{ countLabel }})</p>
      </div>
      <div :class="$style.actionsSections">
        <div v-if="campaign.id !== 'noCampaign' && flags.campaignPurchaseLimit.enabled" :class="$style.purchaseLimit">
          Purchase limit:
          <strong>{{ campaign.purchaseLimit || "Infinite" }}</strong>
        </div>
        <div>
          <OfferByStatus
            v-for="offer in offersByStatus"
            :key="offer.status"
            :status="offer.status"
            :status-count="offer.count"
            :show-label="false"
            :class="$style.statusChip"
          />
        </div>
        <i v-if="isWipVersion && campaign.id !== 'noCampaign'" class="pi pi-pencil" @click.stop="showCampaignModal"></i>
        <i :class="[isContentVisibleLocal ? 'pi pi-chevron-up' : 'pi pi-chevron-down', $style.toggleIcon]"></i>
      </div>
    </div>
    <div v-if="isContentVisibleLocal" :class="$style.contentSlot">
      <slot></slot>
      <section v-if="offers.length === 0" :class="$style.noOffers">
        <div v-if="campaign.name">
          <p>This campaign contains no active offers. You can add offers directly through editing the campaign.</p>
          <Button
            label="Edit campaign"
            icon="pi pi-pencil"
            class="p-button-primary"
            outlined
            @click="showCampaignModal"
          />
        </div>
        <p v-else>All of your offers are linked to a campaign.</p>
      </section>
    </div>
  </div>
  <CampaignForm
    v-if="isCampaignModalVisible"
    :is-visible="isCampaignModalVisible"
    :campaign="campaign"
    @close="handleCampaignModalClose"
  />
</template>

<script setup lang="ts">
import { OfferItem } from "@/graphql";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import { PropType, computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import useFeatureFlags from "@/hooks/useFeatureFlags";
import { Campaign } from "../types/types";
import CampaignForm from "./CampaignForm.vue";
import OfferByStatus from "./OfferByStatus.vue";

const props = defineProps({
  campaign: {
    type: Object as PropType<Campaign>,
    required: true,
  },
  offers: {
    type: Array as PropType<OfferItem[]>,
    default: () => [],
  },
  isContentVisible: {
    type: Boolean,
    default: false,
  },
  isFirstCampaign: {
    type: Boolean,
    default: false,
  },
  selectedCampaigns: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  isWipVersion: {
    type: Boolean,
    default: false,
  },
  // Bulk selection (flag-gated by the parent): shows a campaign-level checkbox that selects the
  // currently-visible (search-filtered) offers of this campaign.
  selectable: {
    type: Boolean,
    default: false,
  },
  selectionState: {
    type: String as PropType<"all" | "some" | "none">,
    default: "none",
  },
  // Unfiltered offer count of the campaign; when search narrows the visible offers the header shows "X of Y".
  totalOffersCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["toggle-select-campaign"]);

const countLabel = computed(() =>
  props.totalOffersCount > props.offers.length
    ? `${props.offers.length} of ${props.totalOffersCount}`
    : `${props.offers.length}`
);

const isContentVisibleLocal = ref(false);
const isCampaignModalVisible = ref(false);

const route = useRoute();

const gameId = ref(route.params.gameId as string);

const { flags } = useFeatureFlags(gameId.value);

const showCampaignModal = () => {
  isCampaignModalVisible.value = true;
};

const handleCampaignModalClose = () => {
  isCampaignModalVisible.value = false;
};

const offersByStatus = computed(() => {
  const groupedOffers = props.offers.reduce((acc: Record<string, OfferItem[]>, offer: OfferItem) => {
    if (!acc[offer.status]) {
      acc[offer.status] = [];
    }
    acc[offer.status].push(offer);
    return acc;
  }, {});

  const publishedCount = groupedOffers.PUBLISHED ? groupedOffers.PUBLISHED.length : 0;
  const draftCount =
    (groupedOffers.DRAFT ? groupedOffers.DRAFT.length : 0) + (groupedOffers.PENDING ? groupedOffers.PENDING.length : 0);
  const terminatingCount =
    (groupedOffers.TERMINATING ? groupedOffers.TERMINATING.length : 0) +
    (groupedOffers.STOPPED ? groupedOffers.STOPPED.length : 0);

  return [
    { status: "published", count: publishedCount },
    { status: "draft", count: draftCount },
    { status: "terminating", count: terminatingCount },
  ];
});

const toggleContent = () => {
  isContentVisibleLocal.value = !isContentVisibleLocal.value;
};

watch(
  () => props.isContentVisible,
  (newValue) => {
    isContentVisibleLocal.value = newValue;
  }
);

watch(
  () => props.selectedCampaigns,
  () => {
    isContentVisibleLocal.value = props.selectedCampaigns.includes(props.campaign.id);
  }
);

onMounted(() => {
  const permalinkOfferId = route.hash.slice(1);
  const hasTargetOffer = !!permalinkOfferId && props.offers.some((offer) => offer.id === permalinkOfferId);

  isContentVisibleLocal.value =
    hasTargetOffer ||
    props.selectedCampaigns.includes(props.campaign.id) ||
    props.isContentVisible ||
    props.isFirstCampaign;
});
</script>

<style module lang="scss">
.collapsorWrapper {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: var(--vd-color-white);
  margin-bottom: 1rem;
}

.headerSlot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--vd-color-text-light-9);
  cursor: pointer;
  padding: 0 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px 6px 0 0;

  p {
    font-weight: bolder;
  }

  .titleSection {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  i {
    cursor: pointer;
    color: var(--vd-color-text-light-2);
    font-size: 1rem;
    margin-left: 1rem;
    padding: 0.25rem;
  }
}

.contentSlot {
  padding: 1rem;
  padding-bottom: 0;
  background-color: var(--vd-color-surface-100);
  border: 1px solid #e5e7eb;
  border-radius: 0 0 6px 6px;
  border-top: none;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.noOffers {
  padding: 1rem;
  margin-bottom: 1rem;
  width: 60%;

  p {
    text-align: center;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.actionsSections {
  display: flex;
  align-items: center;

  .statusChip {
    margin-right: 0.5rem;

    &:last-child {
      margin-right: 0;
    }
  }
}

.purchaseLimit {
  margin-right: 0.5rem;
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  background-color: #e9e9ea;
  color: #333;

  strong {
    margin-left: 0.5rem;
  }
}
</style>
